import React, { FC, useMemo, useState } from 'react';
import { Select } from 'antd';
import { ImportField, ColumnMapping } from './ImportGridTypes';

interface ColumnMapperProps {
	csvHeaders: string[];
	fields: ImportField[];
	onMappingComplete: (mappings: ColumnMapping[]) => void;
}

const autoMapField = (
	csvHeader: string,
	fields: ImportField[],
	claimedKeys: Set<string>
): string | null => {
	const lower = csvHeader.toLowerCase().trim();

	for (const field of fields) {
		if (claimedKeys.has(field.key)) continue;
		if (lower === field.label.toLowerCase()) return field.key;
	}

	for (const field of fields) {
		if (claimedKeys.has(field.key)) continue;
		if (field.alternates) {
			for (const alt of field.alternates) {
				if (lower === alt.toLowerCase()) return field.key;
			}
		}
	}

	for (const field of fields) {
		if (claimedKeys.has(field.key)) continue;
		if (lower === field.key.toLowerCase()) return field.key;
	}

	return null;
};

const thClassName =
	'text-left px-3 py-2 border-0 border-b-2 border-solid border-[#eee] font-semibold';
const tdClassName = 'px-3 py-1.5 border-0 border-b border-solid border-grey-4';

const ColumnMapper: FC<ColumnMapperProps> = ({ csvHeaders, fields, onMappingComplete }) => {
	const [mappings, setMappings] = useState<ColumnMapping[]>(() => {
		const claimedKeys = new Set<string>();
		return csvHeaders.map((header) => {
			const fieldKey = autoMapField(header, fields, claimedKeys);
			if (fieldKey) claimedKeys.add(fieldKey);
			return { csvHeader: header, fieldKey };
		});
	});

	const usedFieldKeys = useMemo(() => {
		return new Set(
			mappings.filter((mapping) => mapping.fieldKey).map((mapping) => mapping.fieldKey)
		);
	}, [mappings]);

	const requiredFields = useMemo(() => {
		return fields.filter(
			(field) =>
				field.constraints && field.constraints.some((constraint) => constraint.type === 'required')
		);
	}, [fields]);

	const unmappedRequired = useMemo(() => {
		return requiredFields.filter((field) => !usedFieldKeys.has(field.key));
	}, [requiredFields, usedFieldKeys]);

	const handleChange = (index: number, fieldKey: string | null) => {
		const updated = [...mappings];
		updated[index] = { ...updated[index], fieldKey };
		setMappings(updated);
	};

	const handleConfirm = () => {
		onMappingComplete(mappings.filter((mapping) => mapping.fieldKey));
	};

	return (
		<div>
			<div className="mb-4 text-[#666]">
				Map your CSV columns to the expected fields. Unmapped columns will be ignored.
			</div>
			{unmappedRequired.length > 0 && (
				<div className="mb-4 rounded border border-solid border-[#ffd591] bg-[#fff7e6] px-3 py-2">
					{`No CSV column maps to: ${unmappedRequired
						.map((field) => field.label)
						.join(', ')}. You can continue anyway and enter ${
						unmappedRequired.length > 1 ? 'these' : 'it'
					} in the grid — every row will be flagged until ${
						unmappedRequired.length > 1 ? 'they are' : 'it is'
					} filled in.`}
				</div>
			)}
			<div className="max-h-96 overflow-y-auto">
				<table className="w-full border-collapse">
					<thead>
						<tr>
							<th className={thClassName}>CSV Column</th>
							<th className={thClassName}>Maps To</th>
						</tr>
					</thead>
					<tbody>
						{mappings.map((mapping, idx) => (
							<tr key={mapping.csvHeader}>
								<td className={tdClassName}>{mapping.csvHeader}</td>
								<td className={tdClassName}>
									<Select
										value={mapping.fieldKey || undefined}
										onChange={(val) => handleChange(idx, val || null)}
										placeholder="-- Ignore --"
										allowClear
										showSearch
										filterOption={(input, option) =>
											String((option && option.children) || '')
												.toLowerCase()
												.includes(input.toLowerCase())
										}
										className="w-full"
										dropdownStyle={{ zIndex: 2000 }}
									>
										{fields.map((field) => (
											<Select.Option
												key={field.key}
												value={field.key}
												disabled={usedFieldKeys.has(field.key) && mapping.fieldKey !== field.key}
											>
												{field.label}
											</Select.Option>
										))}
									</Select>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<div className="mt-4 text-right">
				<button
					onClick={handleConfirm}
					className="cursor-pointer rounded border-none bg-[#1890ff] px-5 py-1.5 text-white"
				>
					Confirm Mapping
				</button>
			</div>
		</div>
	);
};

export default ColumnMapper;
